import React, { useEffect } from "react";
import * as Progress from "@radix-ui/react-progress";
import {
  ArrowLeft,
  CheckCircle,
  Circle,
  ClipboardIcon,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { Toaster } from "../components/ui/toaster";
import { useToast } from "../components/ui/use-toast";
import {
  useGetSwapQuery,
  SwapStatus,
  useGetStepLogsQuery,
} from "../generated/graphql";
import { generateReceipt } from "../lib/generateReceipt";
import { formatDate } from "../lib/formatDate";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../components/ui/tooltip";
import { formatNumberToString } from "../lib/formatNumbers";
export function Swap() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();

  const [{ data, error }, refetchSwap] = useGetSwapQuery({
    variables: {
      swapId: id ?? "0",
    },
    pause: !id,
  });

  const [{ data: stepLogs }, refetchStepLogs] = useGetStepLogsQuery({
    variables: {
      swapId: id ?? "0",
    },
    pause: !id,
  });

  useEffect(() => {
    if (error) {
      console.error(error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      refetchSwap({ requestPolicy: "network-only" });
    }, 10 * 1000);

    return () => clearTimeout(timerId);
  }, [refetchSwap, toast]);

  const steps = [
    { label: "Initiated", action: "Initiated" },
    { label: "Execute Swap", action: "ExecuteSwap" },
    { label: "Transfer Main", action: "TransferToMain" },
    { label: "Transfer Client", action: "TransferToClient" },
    { label: "Complete", action: "Complete" },
  ];

  const swap = data?.getSwap;

  const getCurrentStep = () => {
    if (!swap) return 0;
    if (swap.status === SwapStatus.Completed) return steps.length;

    if (!swap.steps || swap.steps.length === 0) return 0;

    const completedActions = new Set(
      swap.steps
        .filter((step) => step.status === "Completed")
        .map((step) => step.action)
    );

    for (let i = steps.length - 2; i >= 0; i--) {
      if (completedActions.has(steps[i].action)) {
        return i + 1;
      }
    }

    return 0;
  };

  const currentStep = getCurrentStep();
  const progress = (currentStep / (steps.length - 1)) * 100;

  useEffect(() => {
    // Only set up the refresh timer if we shouldn't stop
    if (currentStep < steps.length - 1) {
      const timerId = setTimeout(() => {
        refetchStepLogs({ requestPolicy: "network-only" });
        refetchSwap({ requestPolicy: "network-only" });
      }, 15 * 1000);

      return () => clearTimeout(timerId);
    }
  }, [
    refetchStepLogs,
    refetchSwap,
    data?.getSwap?.steps,
    currentStep,
    steps.length,
  ]);

  console.log(swap);

  return (
    <div className="flex min-h-screen w-full flex-col bg-gray-50">
      <header className="sticky top-0 flex h-16 items-center gap-4 px-4 md:px-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/swaps")}
          className="mr-2 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-semibold text-gray-800">Swap Details</h1>
      </header>

      <main className="flex-1 p-4 md:p-6 lg:p-8 flex flex-col">
        {/* Radix UI Progress Stepper */}
        <div className="max-w-4xl mx-auto py-4 mb-10">
          <Progress.Root
            className="relative overflow-hidden bg-gray-200 rounded-full w-full h-2"
            style={{
              display: "block",
              width: "100%",
              height: "8px",
              minWidth: "400px",
            }}
            value={progress}
          >
            <Progress.Indicator
              className="bg-blue-500 w-full h-full transition-transform duration-[660ms] ease-[cubic-bezier(0.65, 0, 0.35, 1)]"
              style={{
                transform: `translateX(-${100 - progress}%)`,
                display: "block",
                width: "100%",
                height: "100%",
              }}
            />
          </Progress.Root>
          <div className="flex justify-between mt-2">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col items-center relative">
                {index < currentStep ? (
                  <CheckCircle className="h-4 w-4 text-blue-500" />
                ) : index === currentStep ? (
                  <Circle className="h-4 w-4 text-blue-500" />
                ) : (
                  <Circle className="h-4 w-4 text-gray-300" />
                )}
                <span className="text-xs text-gray-600 absolute top-5">
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {swap != null ? (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Swap Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">ID:</span>
                    <span>{swap.id}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Status:</span>
                    <div className="flex items-center">
                      <div
                        className={`h-3 w-3 rounded-full mr-2 ${
                          swap.status === SwapStatus.Completed
                            ? "bg-green-500"
                            : swap.status === SwapStatus.Failed
                            ? "bg-red-500"
                            : swap.status === SwapStatus.Pending
                            ? "bg-yellow-500"
                            : "bg-gray-500"
                        }`}
                      />
                      <span>{swap.status}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 hover:bg-gray-50"
                    disabled={swap.toAmount == null}
                    onClick={() =>
                      generateReceipt({
                        organizationName: swap.organizationName,
                        fromMint: swap.fromMint.name,
                        fromAmount: formatNumberToString(swap.fromAmount) ?? "",
                        toMint: swap.toMint.name,
                        toAmount: formatNumberToString(swap.toAmount) ?? "",
                        date: formatDate(swap.date, "DD-MM-YYYY HH:mm"),
                        swapId: swap.id,
                        txHash: swap.txHash ?? "",
                      })
                    }
                  >
                    Generate Receipt
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Currency Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">From Currency:</span>
                    <span className="flex items-center">
                      {swap.fromMint.name}
                      {(swap.fromMint as { img?: string }).img && (
                        <img
                          src={(swap.fromMint as { img?: string }).img}
                          alt={swap.fromMint.name}
                          className="inline-block h-4 w-4 ml-1"
                        />
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">From Amount:</span>
                    <span>
                      {formatNumberToString(swap.fromAmount) ?? "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">To Currency:</span>
                    <span className="flex items-center">
                      {swap.toMint.name}
                      {(swap.toMint as { img?: string }).img && (
                        <img
                          src={(swap.toMint as { img?: string }).img}
                          alt={swap.toMint.name}
                          className="inline-block h-4 w-4 ml-1"
                        />
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">To Amount:</span>
                    <span>{formatNumberToString(swap.toAmount) ?? "N/A"}</span>
                  </div>
                  <div className="flex justify-between">
                    {(swap.toMint.name === "USDC" ||
                      swap.toMint.name === "USDT") &&
                    (swap.fromMint.name === "USDC" ||
                      swap.fromMint.name === "USDT") ? (
                      <>
                        <span className="font-medium">BPS:</span>
                        <span>
                          {Math.round(
                            ((Number(swap.fromAmount) - Number(swap.toAmount)) /
                              Number(swap.fromAmount)) *
                              10000
                          )}
                        </span>
                      </>
                    ) : null}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Additional Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">Created At:</span>
                    <span>{new Date(swap.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Organization:</span>
                    <span>{swap.organizationName}</span>
                  </div>
                  <div className="flex justify-between flex-col">
                    <span className="font-medium mb-2">Deposit Address:</span>
                    <div className="flex justify-start gap-10 items-center">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              onClick={() => {
                                navigator?.clipboard?.writeText(
                                  swap.depositAddress ?? ""
                                );
                                toast({
                                  title: "Copied to clipboard",
                                  description: "Address copied to clipboard",
                                  variant: "default",
                                });
                              }}
                              style={{
                                width: "100%",
                              }}
                            >
                              {swap.depositAddress}
                              <ClipboardIcon className="ml-2 h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Click to copy</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  <div className="flex justify-between flex-col">
                    <span className="font-medium mb-2">
                      Withdrawal Address:
                    </span>
                    <div className="flex justify-start gap-10 items-center">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              onClick={() => {
                                navigator?.clipboard?.writeText(
                                  swap.endAddress ?? ""
                                );
                                toast({
                                  title: "Copied to clipboard",
                                  description: "Address copied to clipboard",
                                  variant: "default",
                                });
                              }}
                              style={{
                                width: "100%",
                              }}
                            >
                              {swap.endAddress}
                              <ClipboardIcon className="ml-2 h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Click to copy</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Transaction Hash</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    disabled={!swap.txHash}
                    variant="outline"
                    className="w-full text-left flex items-center justify-between"
                    onClick={() => {
                      const explorerUrl =
                        swap.toMint.chain === "BTC"
                          ? process.env.REACT_APP_BTC_EXPLORER_URL
                          : process.env.REACT_APP_ETH_EXPLORER_URL;
                      window.open(`${explorerUrl}${swap.txHash}`, "_blank");
                    }}
                  >
                    {swap.txHash ? (
                      <>
                        <span className="truncate flex-1">{swap.txHash}</span>
                        <ExternalLink className="h-4 w-4 flex-shrink-0 ml-2" />
                      </>
                    ) : (
                      <span className="truncate flex-1">
                        No transaction hash yet.
                      </span>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Step Logs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {stepLogs?.getStepLogs &&
                    stepLogs.getStepLogs.length > 0 ? (
                      stepLogs.getStepLogs.map((log) => (
                        <div
                          key={log.id}
                          className="flex items-start gap-4 border-l-2 border-blue-500 pl-4"
                        >
                          <div className="flex-1">
                            <p className="text-sm text-gray-600">
                              {log.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(log.date).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-4">
                        No step logs available
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center h-full">
            <p className="text-lg text-gray-500">Loading swap details...</p>
          </div>
        )}
      </main>
      <Toaster />
    </div>
  );
}
